// platformSecretsApi — typed fetchers for /v1/platform/secrets/*.
import { apiFetch } from "../../lib/api-client.js";

export type SecretCategory = "oauth" | "email" | "line" | "sdm" | "other";
export type SecretSource = "d1" | "env_fallback" | "missing";
export type SecretStatus = "unset" | "set" | "rotating";

export interface SecretMeta {
  key: string;
  category: SecretCategory;
  category_label: string;
  description: string | null;
  required: boolean;
  multiline: boolean;
  sensitivity: "high" | "normal";
  status: SecretStatus;
  display_hint: string | null;
  updated_by_user_id: string | null;
  updated_at: number | null;
  source: SecretSource;
}

export interface SecretListResponse {
  items: SecretMeta[];
  correlation_id: string | null;
}

export class PlatformSecretsApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export function friendlyPlatformSecretsError(err: unknown): string {
  if (err instanceof PlatformSecretsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "只有平台管理員（super_admin）能管理金鑰。";
    if (err.status === 400) {
      if (err.code === "value_required") return "請輸入金鑰值再儲存。";
      if (err.code === "invalid_key") return "金鑰名稱格式不符（必須是大寫加底線，例如 RESEND_API_KEY）。";
    }
    if (err.status === 500) {
      // The single most common 500 here is the master encryption key
      // missing on the worker — surface the action item directly so the
      // operator doesn't have to dig through logs.
      if (err.code === "master_key_not_set" || err.code === "request_failed")
        return "尚未設定平台主金鑰（PLATFORM_SECRETS_MASTER_KEY）。請先用 `openssl rand -hex 32` 產生 32 bytes 隨機亂數，存進 GitHub repo secret 同名欄位，再去 Actions → bootstrap-secrets → Run workflow（target=all）一鍵推送到所有 worker。";
      return "系統暫時無法處理，請稍後再試。";
    }
    return "金鑰操作失敗，請稍後再試。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<PlatformSecretsApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
  } catch {}
  return new PlatformSecretsApiError(r.status, code, cid);
}

export async function listPlatformSecrets(): Promise<SecretListResponse> {
  const r = await apiFetch("/v1/platform/secrets", { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as SecretListResponse;
}

export async function setPlatformSecret(key: string, value: string, rotating = false): Promise<void> {
  const r = await apiFetch(`/v1/platform/secrets/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ value, rotating }),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function deletePlatformSecret(key: string): Promise<void> {
  const r = await apiFetch(`/v1/platform/secrets/${encodeURIComponent(key)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
